import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Card,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';

import { createUser } from 'services/userService';

const validationSchema = Yup.object({
  userName: Yup.string().required('Username is required'),
  userEmail: Yup.string().email('A valid email must be provided').required('Email is required'),
  userRole: Yup.string().required('User role is required'),
  tenantId: Yup.string().required('Tenant ID is required'),
});

function UserCreate() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const formik = useFormik({
    initialValues: {
      userName: '',
      userEmail: '',
      userRole: 'SystemAdmin',
      tenantId: 'system_admins',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setSubmitting(true);
        await createUser(values);
        setSnackbar({ open: true, message: 'Successfully created new user!', severity: 'success' });
        setTimeout(() => navigate('/users'), 1500);
      } catch (err) {
        console.error('Error creating user:', err);
        setSnackbar({ open: true, message: 'An unexpected error occurred!', severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="h6" mb={1}>Create User</SoftTypography>
              <SoftTypography variant="body2" color="text" mb={3}>
                Upon submission, a new user account will be created and we will send an email to the provided address with login instructions.
              </SoftTypography>
              <form onSubmit={formik.handleSubmit}>
                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">Username *</SoftTypography>
                  <SoftInput
                    placeholder="Username"
                    name="userName"
                    value={formik.values.userName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.userName && Boolean(formik.errors.userName)}
                  />
                  {formik.touched.userName && formik.errors.userName && (
                    <SoftTypography variant="caption" color="error">{formik.errors.userName}</SoftTypography>
                  )}
                </SoftBox>

                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">Email *</SoftTypography>
                  <SoftInput
                    placeholder="user@example.com"
                    name="userEmail"
                    type="email"
                    value={formik.values.userEmail}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.userEmail && Boolean(formik.errors.userEmail)}
                  />
                  {formik.touched.userEmail && formik.errors.userEmail && (
                    <SoftTypography variant="caption" color="error">{formik.errors.userEmail}</SoftTypography>
                  )}
                </SoftBox>

                <SoftBox mb={2}>
                  <SoftTypography variant="caption" fontWeight="bold">User Role *</SoftTypography>
                  <SoftInput
                    placeholder="User Role"
                    name="userRole"
                    value={formik.values.userRole}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.userRole && Boolean(formik.errors.userRole)}
                  />
                  {formik.touched.userRole && formik.errors.userRole && (
                    <SoftTypography variant="caption" color="error">{formik.errors.userRole}</SoftTypography>
                  )}
                </SoftBox>

                <SoftBox mb={3}>
                  <SoftTypography variant="caption" fontWeight="bold">Tenant ID *</SoftTypography>
                  <SoftInput
                    placeholder="Tenant Id"
                    name="tenantId"
                    value={formik.values.tenantId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.tenantId && Boolean(formik.errors.tenantId)}
                  />
                  {formik.touched.tenantId && formik.errors.tenantId && (
                    <SoftTypography variant="caption" color="error">{formik.errors.tenantId}</SoftTypography>
                  )}
                </SoftBox>

                <SoftBox display="flex" gap={2}>
                  {!submitting ? (
                    <>
                      <SoftButton type="submit" variant="gradient" color="info" disabled={!formik.isValid}>
                        Create
                      </SoftButton>
                      <SoftButton variant="gradient" color="secondary" onClick={() => navigate('/users')}>
                        Cancel
                      </SoftButton>
                    </>
                  ) : (
                    <CircularProgress size={24} />
                  )}
                </SoftBox>
              </form>
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default UserCreate;
